/**
 * SiteReliabilitySummary
 *
 * The site-level hint, shown once: verdict, what it means for the bays, the delay
 * evidence and how fresh the underlying data is. A last shot that is days or weeks
 * old is shown next to the verdict, because the backend never flags a quiet site.
 */
import type { BayReliability, SiteReliabilityHint } from '../../models/reliabilityModels';
import {
  describeLastShot,
  levelLabel,
  levelTone,
  siteExplanation,
  siteHeadline,
} from '../../utils/reliabilityPresentation';
import { formatImpactTime } from '../../utils/latencyClassifier';
import { StatusPill } from './StatusPill';
import { DelayEvidenceMeter } from './DelayEvidenceMeter';

interface SiteReliabilitySummaryProps {
  readonly site: SiteReliabilityHint;
  readonly bays: readonly BayReliability[];
}

export function SiteReliabilitySummary({ site, bays }: SiteReliabilitySummaryProps): JSX.Element {
  const lastShot = describeLastShot(site.lastShotAt, new Date());

  return (
    <div className="reliability-summary">
      <div className="reliability-summary__verdict">
        <StatusPill tone={levelTone(site.level)} label={levelLabel(site.level)} />
        <p className="reliability-summary__headline">{siteHeadline(site, bays)}</p>
        {lastShot.isStale && (
          <p
            className="reliability-summary__last-shot"
            title={site.lastShotAt ? new Date(site.lastShotAt).toLocaleString() : undefined}
          >
            {lastShot.text}
          </p>
        )}
      </div>
      <p className="reliability-summary__explanation">{siteExplanation(site)}</p>

      <div className="reliability-summary__evidence">
        {site.delay && <DelayEvidenceMeter delay={site.delay} />}

        <div className="reliability-freshness">
          <h4 className="reliability-section-title">Freshness</h4>
          <dl className="reliability-facts">
            <div className="reliability-facts__row">
              <dt>Upstream status confirmed</dt>
              <dd>
                {site.statusSnapshotAt ? formatImpactTime(site.statusSnapshotAt) : 'Never received'}
              </dd>
            </div>
            <div className="reliability-facts__row">
              <dt>Last shot</dt>
              <dd>{site.lastShotAt ? formatImpactTime(site.lastShotAt) : 'None delivered yet'}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}
